import React, { useState, useEffect } from "react";
import { Calendar } from "lucide-react";
import { Constants } from "../utils/constants";
import HTitleInput from "./HTitleInput";
import HFormModal from "./HFormModal";
import apiService from "../services/apiService";
import { showApiMessage } from "../utils/messageHandler";
import * as localService from "../services/localService";

const HEditAnnouncementModal = ({ isOpen, onClose, announcement, onSuccess }) => {
  const [title, setTitle] = useState("");
  const [message, setMessage] = useState("");
  const [datetimeToSend, setDatetimeToSend] = useState(new Date());
  const [isLoading, setIsLoading] = useState(false);

  // Set initial values when announcement data changes
  useEffect(() => {
    if (announcement) {
      setTitle(announcement.title || "");
      setMessage(announcement.message || "");
      // Ensure we always store a valid Date object, as API data might be a string
      const dateValue = announcement.datetimeToSend ? new Date(announcement.datetimeToSend) : new Date();
      setDatetimeToSend(isNaN(dateValue.getTime()) ? new Date() : dateValue);
    }
  }, [announcement]);

  const handleDateChange = e => {
    setDatetimeToSend(new Date(e.target.value));
  };

  const handleSave = async () => {
    if (isLoading) return;
    try {
      setIsLoading(true);
      const user = localService.getUser();
      const userId = user?.id?.toString();
      if (!userId) {
        showApiMessage({ response: { status: 400, data: { message: "User ID not found" } } }, "update announcement");
        return;
      }

      const payload = {
        title,
        message,
        datetimeToSend: datetimeToSend.toISOString(),
        actionType: "announcement",
        data: {},
      };

      const resp = await apiService.updateAnnouncement(announcement.id, payload);
      showApiMessage(resp, "update announcement");
      if (onSuccess) onSuccess();
      onClose();
    } catch (error) {
      showApiMessage(error, "update announcement");
    } finally {
      setIsLoading(false);
    }
  };

  const formatDateTime = date => {
    const d = date instanceof Date ? date : new Date(date);
    if (isNaN(d.getTime())) return "Not scheduled";

    return d.toLocaleString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "numeric",
      minute: "numeric",
      hour12: true,
    });
  };

  return (
    <HFormModal
      isOpen={isOpen}
      onClose={onClose}
      title="Edit Announcement"
      primaryActionLabel={isLoading ? "Saving..." : "Save Changes"}
      secondaryActionLabel="Cancel"
      onPrimaryAction={handleSave}
      primaryActionDisabled={isLoading}
      maxHeight="90vh"
    >
      <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-1">
        <HTitleInput value={title} onChange={setTitle} />

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Message</label>
          <textarea
            value={message}
            onChange={e => setMessage(e.target.value)}
            placeholder="Write your announcement message..."
            className="w-full p-3 bg-gray-50 rounded-xl border border-gray-200 text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 min-h-[80px] resize-y"
          />
        </div>

        {/* Schedule Date and Time */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">When to Send</label>
          <div className="flex items-center gap-2">
            <div className="flex-1">
              <input
                type="datetime-local"
                value={
                  datetimeToSend instanceof Date && !isNaN(datetimeToSend.getTime())
                    ? datetimeToSend.toISOString().slice(0, 16)
                    : new Date().toISOString().slice(0, 16)
                }
                onChange={handleDateChange}
                className="w-full p-2 bg-gray-50 rounded-xl border border-gray-200 text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div
              className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
              style={{ backgroundColor: Constants.HOST_EXPERIENCE_BASE_COLOR }}
            >
              <Calendar className="w-5 h-5 text-white" />
            </div>
          </div>
          <div className="text-xs text-gray-500 mt-1">Scheduled for: {formatDateTime(datetimeToSend)}</div>
        </div>
      </div>
    </HFormModal>
  );
};

export default HEditAnnouncementModal;
